
import { useToast } from "@/hooks/use-toast";

interface UseCategoryManagementProps {
  handleRemoveCategory: (categoryName: string) => void;
  handleUpdateCategory: (categoryName: string, newName: string, newIconSrc?: string) => void;
  handleAddNewCategoryToSystem: (categoryName: string, iconSrc?: string) => void;
  handleCategoryDeleted: (categoryName: string) => void;
  handleCategoryRenamed: (oldName: string, newName: string) => void;
  setSelectedCategory: (category: string | null) => void;
  selectedCategory: string | null;
}

export const useCategoryManagement = ({
  handleRemoveCategory,
  handleUpdateCategory,
  handleAddNewCategoryToSystem,
  handleCategoryDeleted,
  handleCategoryRenamed,
  setSelectedCategory,
  selectedCategory,
}: UseCategoryManagementProps) => {
  const { toast } = useToast();

  const handleCategoryRemove = (categoryName: string) => {
    try {
      handleRemoveCategory(categoryName);
      handleCategoryDeleted(categoryName);
      if (selectedCategory === categoryName) {
        setSelectedCategory(null);
      }
      toast({
        title: "Category deleted",
        description: "Category has been removed from all habits",
      });
    } catch (error) {
      console.error('Error removing category:', error);
      toast({
        title: "Error",
        description: "Failed to remove category",
        variant: "destructive",
      });
    }
  };

  const handleCategoryUpdate = (categoryName: string, newName: string, newIconSrc?: string) => {
    if (!newName.trim() || (categoryName === newName.trim() && newIconSrc === undefined)) return;

    try {
      handleUpdateCategory(categoryName, newName.trim(), newIconSrc);
      
      if (categoryName !== newName.trim()) {
        handleCategoryRenamed(categoryName, newName.trim());
        if (selectedCategory === categoryName) {
          setSelectedCategory(newName.trim());
        }
      }
      
      toast({
        title: "Category updated",
        description: "Category has been updated successfully",
      });
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: "Error",
        description: "Failed to update category",
        variant: "destructive",
      });
    }
  };

  const handleAddCategory = (categoryName: string, iconSrc?: string) => {
    try {
      handleAddNewCategoryToSystem(categoryName, iconSrc);
      toast({
        title: "Category added",
        description: "New category has been created",
      });
    } catch (error) {
      console.error('Error adding category:', error);
      toast({
        title: "Error",
        description: "Failed to add category",
        variant: "destructive",
      });
    }
  };

  return {
    handleCategoryRemove,
    handleCategoryUpdate,
    handleAddCategory,
  };
};
